import { CancellationRecord, groupCancellationHistory } from '../../../../Component/tool/groupCancellationHistory';
import UserModalHeader from '../../../../Component/UI/UserModalHeader';
import ContentState from "../../../../Component/UI/ContentState";
import { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

import api from "../../../../Component/axios_Interceptor/api";

import url, { SERVER_ADDRESS } from "../../../../Component/server";

import { amountFormat } from "../../../../Component/tool/Tools";

import Buy_Cancel_Modal from "./Buy_Cancel_Modal";



interface PurchaseListModalProps {
  visible: boolean;

  handleModal: React.Dispatch<React.SetStateAction<boolean>>;

  seq: number;

  ticketCount?: number;

  onOrderChanged?: () => void;

  refreshKey?: number;
}



interface OrderSummary {
  seq: number;

  store_seq: number;

}

interface OrderDetail {
  seq: number;
  product_seq?: number;
  individual_delivery?: boolean;
  individual_delivery_price?: number;
  option_seq?: number | null;
  product_num: number | string | null;
  product_name: string;

  option_name: string;

  order_cnt: number;

  cancel_cnt?: number;
  cancel_price?: number;
  order_price: number;
  coupon_discount_amount?: number;

}


interface OptionGroup {
  key: string;
  option_name: string;
  order_cnt: number;
  items: OrderDetail[];
}

interface ProductGroup {
  key: string;
  product_num: OrderDetail["product_num"];
  product_name: string;
  options: OptionGroup[];
  total: number;
  discount: number;
  individualDelivery: boolean;
  deliveryFee: number;
}


const PurchaseListModal = ({ visible, handleModal, seq, ticketCount = 0, onOrderChanged, refreshKey }: PurchaseListModalProps) => {
  const [order, setOrder] = useState<OrderSummary | null>(null);
  const [details, setDetails] = useState<OrderDetail[]>([]);
  const [cancellations, setCancellations] = useState<CancellationRecord[]>([]);
  const [cancellationReadSupported, setCancellationReadSupported] = useState(true);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [cancelItems, setCancelItems] = useState<OrderDetail[]>([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"purchases" | "cancellations">("purchases");
  const [noTicketOpen, setNoTicketOpen] = useState(false);
  const requestVersion = useRef(0);


  const loadOrders = useCallback(async () => {
    const version = ++requestVersion.current;

    setLoading(true);
    setLoadError("");
    setOrder(null);
    setDetails([]); setCancellations([]);
    setCancellationReadSupported(true);
    setDiscountAmount(0);
    try {

      const { data } = await api.get(url.Communication_url.order_info, { params: { live_seq: seq } });
      if (version !== requestVersion.current) return;
      setOrder(data?.order || null);
      setCancellations(Array.isArray(data?.cancellations) ? data.cancellations : []);
      setCancellationReadSupported(data?.cancellation_read_supported !== false);
      setDiscountAmount(Number(data?.discount_amount || 0));
      setDetails(Array.isArray(data?.order_detail) ? data.order_detail.map((item: OrderDetail) => ({ ...item,
        order_cnt: Math.max(0, Number(item.order_cnt) - Number(item.cancel_cnt || 0)),
        order_price: Math.max(0, Number(item.order_price) - Number(item.cancel_price || 0)),
      })).filter((item: OrderDetail) => item.order_cnt > 0) : []);
    } catch (error: any) {
      if (version !== requestVersion.current) return;
      if (error?.response?.status !== 404) setLoadError("구매 목록을 불러오지 못했습니다. 다시 시도해주세요.");
    } finally {

      if (version === requestVersion.current) setLoading(false);

    }

  }, [seq]);

  useEffect(() => {

    if (visible) void loadOrders();
    return () => { requestVersion.current += 1; };
  }, [visible, refreshKey, loadOrders]);

  useEffect(() => { setNoTicketOpen(false); setActiveTab("purchases"); }, [visible, seq]);

  const unreadCancellations = cancellationReadSupported ? cancellations.filter(item => item.source === 'admin' && !item.read_at) : [];
  useEffect(() => {
    if (!visible || activeTab !== 'cancellations' || loading || !unreadCancellations.length) return;
    const throughSeq = Math.max(...unreadCancellations.map(item => item.seq));
    const version = requestVersion.current;
    let active = true;
    void api.patch(`${SERVER_ADDRESS}orders/cancellations/${seq}/read/${throughSeq}`).then(({ data }) => {
      if (!active || version !== requestVersion.current || !data?.read_at) return;
      setCancellations(current => current.map(item => item.source === 'admin' && item.seq <= throughSeq
        ? { ...item, read_at: data.read_at } : item));
    }).catch(error => {
      if (active && version === requestVersion.current && error?.response?.data?.code === 'CANCELLATION_READ_UNAVAILABLE') {
        setCancellationReadSupported(false);
      }
      // Other failures retain the unread marker so the next visit can retry.
    });
    return () => { active = false; };
  }, [visible, activeTab, loading, cancellations, cancellationReadSupported, seq]);

  const groups = details.reduce<ProductGroup[]>((result, item) => {
    const key = JSON.stringify(item.product_seq ?? [item.product_num, item.product_name]);
    let group = result.find(entry => entry.key === key);
    if (!group) {
      group = { key, product_num: item.product_num, product_name: item.product_name, options: [], total: 0, discount: 0, individualDelivery: false, deliveryFee: 0 };
      result.push(group);
    }

    const optionName = item.option_name || "기본";
    const optionKey = JSON.stringify(item.option_seq ?? optionName);
    let option = group.options.find(entry => entry.key === optionKey);
    if (!option) {
      option = { key: optionKey, option_name: optionName, order_cnt: 0, items: [] };
      group.options.push(option);
    }
    option.items.push(item);
    option.order_cnt += Number(item.order_cnt) || 0;
    group.total += Number(item.order_price) || 0;
    group.discount += Math.max(0, Math.min(Number(item.order_price) || 0, Number(item.coupon_discount_amount) || 0));
    if (item.individual_delivery && !group.individualDelivery) {
      group.individualDelivery = true;
      group.deliveryFee = Math.max(0, Number(item.individual_delivery_price || 0));
    }
    return result;
  }, []);
  const total = details.reduce((sum, item) => sum + (Number(item.order_price) || 0), 0);
  const individualDeliveryTotal = groups.reduce((sum, group) => sum + group.deliveryFee, 0);


  const renderItems = () => <Items>
                    {groups.map((group, index) => <Group key={group.key}>
                      <ProductNumber>No.{group.product_num ?? index + 1}</ProductNumber>
                      <ProductName>{group.product_name}</ProductName>
                      {group.options.map(option => <OptionRow key={option.key}>
                        <OptionText>· {option.option_name} / {option.order_cnt}개</OptionText>
                        <CancelButton type="button" onClick={() => {
                          if (ticketCount < 1) { setNoTicketOpen(true); return; }
                          setCancelItems(option.items.map(item => ({ ...item, product_num: item.product_num ?? index + 1 })));
                          setCancelOpen(true);
                        }}>주문 취소</CancelButton>
                      </OptionRow>)}
                      {group.individualDelivery && <IndividualDeliveryFee>개별 배송비 {amountFormat(group.deliveryFee)}원</IndividualDeliveryFee>}
                      {group.discount > 0 && <ProductDiscount><span>할인 쿠폰</span><strong>-{amountFormat(group.discount)}원</strong></ProductDiscount>}
                      <GroupTotal>{amountFormat(group.total - group.discount + group.deliveryFee)}원</GroupTotal>
                    </Group>)}
                  </Items>;

  const closeModal = () => {
    setCancelOpen(false);
    setNoTicketOpen(false);
    handleModal(false);
  };



  return (

    <Backdrop $visible={visible} onClick={closeModal}>
      <Sheet role="dialog" aria-modal="true" aria-label="구매 목록" onClick={event => event.stopPropagation()}>
        <UserModalHeader title="구매 목록" onClose={closeModal} />
        <PurchaseTabs role="tablist" aria-label="구매 내역 구분">
          {([['purchases', '구매 내역'], ['cancellations', '취소 내역']] as const).map(([tab, label]) =>
            <button key={tab} type="button" role="tab" id={`purchase-${tab}-tab`}
              aria-selected={activeTab === tab} aria-controls="purchase-history-panel"
              onClick={() => setActiveTab(tab)}>{label}{tab === 'cancellations' && unreadCancellations.length > 0
                && <UnreadDot role="img" aria-label="읽지 않은 관리자 취소" />}</button>)}
        </PurchaseTabs>
        <Body role="tabpanel" id="purchase-history-panel" aria-labelledby={`purchase-${activeTab}-tab`}>
          {loading ? <ContentState compact kind="loading" icon="bag" title="구매 목록 확인 중..." />
            : loadError ? <ContentState compact kind="error" icon="bag" title={loadError} action={{ label: "다시 시도", onClick: loadOrders }} />
              : activeTab === 'purchases' ? <>
                {details.length === 0 ? <ContentState compact icon="bag" title={cancellations.length ? "현재 구매 중인 상품이 없습니다." : "구매 내역이 없습니다."} description={cancellations.length ? "취소 내역은 취소 내역 탭에서 확인할 수 있어요." : "라이브에서 구매한 상품을 이곳에서 확인할 수 있어요."} />
                  : renderItems()}
              </> : cancellations.length === 0 ? <ContentState compact icon="bag" title="취소 내역이 없습니다." />
                : <CancellationHistory aria-label="취소 내역">
                  {groupCancellationHistory(cancellations).map(item => <section key={item.seq}>
                    <div className="cancellation-heading"><strong>{item.source === 'admin' ? item.pgCompleted ? '관리자 결제 취소' : '관리자 취소' : '고객 취소'}</strong>
                      {item.source === 'admin' && <span>취소 사유: {item.reasons.join(' · ') || '미기록'}</span>}</div>
                    {item.bankPending && <p>환불 대기</p>}{item.bankCompleted && <p>환불 완료</p>}
                    <p>{item.product_name}{item.option_names.length ? ` · ${item.option_names.join(' / ')}` : ''}</p>
                    <span>{item.quantity}개 · {amountFormat(item.amount)}원</span>
                  </section>)}
                </CancellationHistory>}
          {!loading && !loadError && activeTab === 'cancellations' && !cancellationReadSupported &&
            <ReadStateNotice role="status">취소 내역은 확인할 수 있지만, 읽음 표시는 아직 사용할 수 없습니다.</ReadStateNotice>}
        </Body>
        {activeTab === 'purchases' && <SummaryFooter>
          {details.length > 0 && <TicketCount>보유 취소 티켓: {ticketCount}</TicketCount>}
          <Total><span>총 구매금액</span><strong>{loading || loadError ? '—' : `${amountFormat(Math.max(0, total - discountAmount) + individualDeliveryTotal)}원`}</strong></Total>
        </SummaryFooter>}
      </Sheet>
      <Buy_Cancel_Modal ticketCount={ticketCount} visible={cancelOpen} handleModal={setCancelOpen}
        item={cancelItems[0] || null} items={cancelItems} orderSeq={order?.seq || 0} liveSeq={seq} storeSeq={order?.store_seq || 0}
        onCancelSuccess={() => { void loadOrders(); onOrderChanged?.(); }} />
      {noTicketOpen && <Backdrop $visible style={{ zIndex: 100, alignItems: "center" }}
        onClick={event => { event.stopPropagation(); setNoTicketOpen(false); }}>
        <TicketNotice role="alertdialog" aria-modal="true" aria-label="취소 티켓 안내"
          onClick={event => event.stopPropagation()}
          onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); setNoTicketOpen(false); } }}>
          <p>보유 취소 티켓이 없습니다.</p>
          <TicketNoticeButton type="button" autoFocus onClick={() => setNoTicketOpen(false)}>확인</TicketNoticeButton>
        </TicketNotice>
      </Backdrop>}
    </Backdrop>
  );

};



export default PurchaseListModal;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Backdrop = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 99;
  display: ${({ $visible }) => $visible ? "flex" : "none"};
  justify-content: center;
  align-items: flex-end;
  background: #00000085;
  & & { background: rgba(0, 0, 0, .32); }
`;

const Sheet = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 500px;
  height: min(800px, 95dvh);
  max-height: 95dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px 16px 0 0;
  background: var(--user-surface);
  color: var(--user-text);
  animation: ${slideUp} 0.25s ease-out;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 20px 17px 24px;
`;

const ReadStateNotice = styled.p`
  margin: 16px 0 0; color: var(--user-text-muted); font-size: 13px; line-height: 1.5;
`;
const CancellationHistory = styled.div`
  margin-top: 28px; color: var(--user-text-muted);
  h3 { color: var(--user-text); font-size: 16px; margin: 0 0 12px; }
  section { padding: 16px 0; border-top: 1px solid var(--user-border); }
  .cancellation-heading { display:flex; align-items:baseline; flex-wrap:wrap; gap:8px; }
  strong { color: var(--user-text); font-size: 14px; }
  p { margin: 8px 0; font-size: 15px; overflow-wrap: anywhere; }
  span { font-size: 14px; font-variant-numeric: tabular-nums; }
`;
const SummaryFooter = styled.div`
  flex: none;
  padding: 14px 17px calc(20px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--user-border);
  background: var(--user-surface);
`;



const Items = styled.div`
  border: 1px solid var(--user-border);
  border-radius: 11px;
  padding: 0 20px;
  background: var(--user-surface-muted);
`;

const Group = styled.section`
  padding: 24px 0;
  &:not(:last-child) { border-bottom: 1px solid var(--user-border); }
`;

const ProductNumber = styled.div`
  margin-bottom: 5px;
  color: var(--user-text-muted);
  font-size: 13px;
`;

const ProductName = styled.div`
  color: var(--user-text);
  font-size: 17px;
  font-family: var(--font-Pretendard-Bold);
  word-break: break-word;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
`;

const OptionText = styled.span`
  min-width: 0;
  flex: 1;
  color: var(--user-text);
  font-size: 15px;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const CancelButton = styled.button`
  flex: none;
  min-width: 86px;
  min-height: 44px;
  padding: 0 10px;
  border: 1px solid var(--user-border);
  border-radius: 7px;
  background: var(--user-surface);
  color: var(--user-text);
  font-size: 14px;
  cursor: pointer;
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
`;

const TicketNotice = styled.div`
  width: calc(100% - 40px);
  max-width: 432px;
  box-sizing: border-box;
  padding: 24px;
  border-radius: 24px;
  border: 1px solid var(--user-border);
  background: var(--user-surface-raised);
  box-shadow: 0 16px 48px rgba(0, 0, 0, .3);
  color: var(--user-text);
  text-align: center;
  p { margin: 20px 0 32px; font-size: 16px; line-height: 1.5; }
`;

const TicketNoticeButton = styled.button`
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(90deg, #7955f0, #3209b9);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
`;

const GroupTotal = styled.div`
  margin-top: 19px;
  text-align: right;
  color: var(--user-text);
  font-size: 20px;
  font-family: var(--font-Pretendard-Bold);
`;

const TicketCount = styled.div`
  margin-top: 10px;
  text-align: right;
  color: var(--user-error);
  font-size: 14px;
`;

const Total = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 16px;
  font-family: var(--font-Pretendard-Bold);
  strong { color: var(--user-error); font-size: 23px; white-space: nowrap; }
`;

const PurchaseTabs = styled.div`
  display: flex; flex: none; border-bottom: 1px solid var(--user-border); padding: 0 17px;
  button { flex: 1; min-height: 48px; border: 0; border-bottom: 2px solid transparent;
    background: transparent; color: var(--user-text-muted); font: inherit; cursor: pointer; }
  button[aria-selected="true"] { border-bottom-color: var(--user-accent); color: var(--user-accent); font-weight: 600; }
  button:focus-visible { outline: 2px solid var(--user-accent); outline-offset: -2px; }
`;
const IndividualDeliveryFee = styled.div`
  margin-top: 14px; text-align: right; color: var(--user-text-muted); font-size: 14px;
`;

const ProductDiscount = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  color: var(--user-accent);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  strong { font-weight: 600; white-space: nowrap; }
`;

const UnreadDot = styled.span`
  display: inline-block; width: 7px; height: 7px; margin-left: 6px; border-radius: 50%;
  background: var(--user-error); vertical-align: super;
`;
